import React, { useCallback, useEffect, useState } from "react";
import { Cpu, Save, AlertTriangle, RefreshCw, Loader2, Power } from "lucide-react";
import { toast } from "sonner";
import { adminGetAutomation, adminPatchAutomation } from "../../lib/api";

const MODES = [
  { key: "manual", label: "A — Manual", desc: "AI does nothing automatically. Everything waits for you." },
  { key: "assisted", label: "B — Assisted", desc: "AI assists on-demand. Nothing publishes automatically." },
  { key: "semi_autonomous", label: "C — Semi-autonomous", desc: "AI may ingest, lead, investigate, fill gaps, polish, stage. Publishing remains explicit." },
  { key: "autonomous_guarded", label: "D — Autonomous (guarded)", desc: "All of the above + can RECOMMEND publish under strict rules. Auto-publish itself stays OFF in v1." },
];

const TOGGLES = [
  ["allow_auto_radar", "Auto-ingest radar"],
  ["allow_auto_lead_creation", "Auto-create leads"],
  ["allow_auto_investigate", "Auto-investigate"],
  ["allow_auto_fill_gaps", "Auto-fill gaps"],
  ["allow_auto_polish", "Auto-polish drafts"],
  ["allow_auto_stage_approved", "Auto-stage approved drafts"],
  ["allow_auto_publish", "Auto-publish (locked OFF in v1)"],
];

export const AdminAutomation = ({ token, userToken = null }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setS(await adminGetAutomation(token, userToken)); }
    catch { toast.error("Failed to load automation settings"); }
  }, [token, userToken]);
  useEffect(() => { load(); }, [load]);

  if (!s) return <div className="text-[#A39E93] font-mono text-xs">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try { setS(await adminPatchAutomation(token, s, userToken)); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-automation">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Cpu size={13} /> // AUTOMATION
          </div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Editorial Autopilot</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Configure how much The Archive runs on its own. Auto-publish is locked OFF in v1.
            Recurring automation lands in v2 — but mode + guardrail settings persist now so the
            v2 jobs slot in clean.
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          <RefreshCw size={12} /> Reload
        </button>
      </div>

      <div className={"border-2 px-4 py-3 mb-4 " + (s.kill_switch ? "border-[#D92D20] bg-[#1a0808]" : "border-[#2D2A26] bg-[#131313]")}>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="kill-switch-row">
          <input type="checkbox" checked={!!s.kill_switch}
            onChange={(e) => setS({ ...s, kill_switch: e.target.checked })}
            data-testid="kill-switch" className="accent-[#D92D20] w-4 h-4" />
          <Power size={14} className={s.kill_switch ? "text-[#D92D20]" : "text-[#A39E93]"} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// GLOBAL KILL SWITCH</div>
            <div className="text-sm text-[#A39E93]">When engaged, all automation pauses. On-demand AI actions also block.</div>
          </div>
        </label>
      </div>

      <section className="border border-[#2D2A26] bg-[#131313] p-5 mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// OPERATING MODE</div>
        <div className="space-y-2">
          {MODES.map((m) => (
            <label key={m.key} className="flex items-start gap-3 cursor-pointer border border-[#2D2A26] hover:border-[#454038] p-3" data-testid={`mode-${m.key}`}>
              <input type="radio" name="mode" checked={s.mode === m.key}
                onChange={() => setS({ ...s, mode: m.key })} className="mt-1 accent-[#D92D20]" />
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E4DFD3]">{m.label}</div>
                <div className="text-xs text-[#A39E93]">{m.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </section>

      <section className="border border-[#2D2A26] bg-[#131313] p-5 mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// PER-FEATURE TOGGLES</div>
        <div className="grid md:grid-cols-2 gap-2">
          {TOGGLES.map(([k, label]) => {
            const locked = k === "allow_auto_publish";
            return (
              <label key={k} className={"flex items-center gap-2 border border-[#2D2A26] px-3 py-2 " + (locked ? "opacity-50" : "cursor-pointer")} data-testid={`toggle-${k}`}>
                <input type="checkbox" disabled={locked} checked={!!s.toggles?.[k]}
                  onChange={(e) => setS({ ...s, toggles: { ...s.toggles, [k]: e.target.checked } })}
                  className="accent-[#D92D20]" />
                <span className="text-sm text-[#E4DFD3]">{label}</span>
              </label>
            );
          })}
        </div>
      </section>

      <section className="border border-[#2D2A26] bg-[#131313] p-5 mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// GUARDRAILS</div>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-[#A39E93]">
          <label>Min source count
            <input type="number" min={1} max={50} value={s.guardrails?.min_source_count || 5}
              onChange={(e) => setS({ ...s, guardrails: { ...s.guardrails, min_source_count: Number(e.target.value) } })}
              className="block w-full mt-1 bg-[#0F0F0F] border border-[#2D2A26] px-2 py-1 text-[#E4DFD3]" />
          </label>
          <label>Min confidence
            <select value={s.guardrails?.min_confidence || "mixed_source_base"}
              onChange={(e) => setS({ ...s, guardrails: { ...s.guardrails, min_confidence: e.target.value } })}
              className="block w-full mt-1 bg-[#0F0F0F] border border-[#2D2A26] px-2 py-1 text-[#E4DFD3]">
              <option value="weak_source_base">weak</option>
              <option value="mixed_source_base">mixed</option>
              <option value="strong_source_base">strong</option>
            </select>
          </label>
          <label>Max contradictions for publish
            <input type="number" min={0} max={20} value={s.guardrails?.max_contradictions_for_publish ?? 2}
              onChange={(e) => setS({ ...s, guardrails: { ...s.guardrails, max_contradictions_for_publish: Number(e.target.value) } })}
              className="block w-full mt-1 bg-[#0F0F0F] border border-[#2D2A26] px-2 py-1 text-[#E4DFD3]" />
          </label>
          <label className="flex items-center gap-2 mt-5">
            <input type="checkbox" checked={!!s.guardrails?.weak_source_base_forced_hold}
              onChange={(e) => setS({ ...s, guardrails: { ...s.guardrails, weak_source_base_forced_hold: e.target.checked } })}
              className="accent-[#D92D20]" />
            <span>Force weak-source cases to Hold</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!s.guardrails?.duplicate_overlap_blocks_autopublish}
              onChange={(e) => setS({ ...s, guardrails: { ...s.guardrails, duplicate_overlap_blocks_autopublish: e.target.checked } })}
              className="accent-[#D92D20]" />
            <span>Duplicate overlap blocks auto-publish</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!s.guardrails?.breaking_news_no_autopublish}
              onChange={(e) => setS({ ...s, guardrails: { ...s.guardrails, breaking_news_no_autopublish: e.target.checked } })}
              className="accent-[#D92D20]" />
            <span>Breaking-news never auto-publishes</span>
          </label>
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-[#CBB892]">
          <AlertTriangle size={11} /> Categories requiring human sign-off: <span className="font-mono">{(s.guardrails?.sensitive_categories_require_human || []).join(", ") || "—"}</span>
        </div>
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="automation-save"
          className="inline-flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
          {busy ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />} Save settings
        </button>
      </div>
    </div>
  );
};

export default AdminAutomation;
